import { useCallback, useMemo, useState } from "react";

import { addMonth } from "@/lib/dayjs/add/add-month";
import { getFirstDayOfMonth } from "@/lib/dayjs/get-first-day-of-month";

import { createCalendarData } from "./utils";

import type { CalendarType } from "./utils/types";
import type { CalendarContextValue } from "../../../context/calendar/calendar-context";

type Props = Pick<CalendarContextValue, "selected" | "numberOfMonths">;

export const useInitialize = ({
	selected,
	numberOfMonths,
}: Props): {
	calendarData: CalendarType;
	onSetNextMonth: () => void;
	onSetPreviousMonth: () => void;
} => {
	// 選択された日付がある場合はその日付を初期値とする
	const initialDate = useMemo(
		() => (selected && typeof selected !== "string" && "from" in selected ? selected.from : selected),
		[selected],
	);

	const initialCalendarData = useMemo(
		() => createCalendarData({ initialDate, numberOfMonths }),
		[initialDate, numberOfMonths],
	);

	const [calendarData, setCalendarData] = useState<CalendarType>(initialCalendarData);

	const onSetNextMonth = useCallback(() => {
		const nextMonth = addMonth({ date: calendarData.currentMonthFirstDay, amount: 1 });
		const nextMonthFirstDay = getFirstDayOfMonth({ date: nextMonth });
		const newCalendarData = createCalendarData({ initialDate: nextMonthFirstDay, numberOfMonths });

		setCalendarData(newCalendarData);
	}, [calendarData, numberOfMonths]);

	const onSetPreviousMonth = useCallback(() => {
		const previousMonth = addMonth({ date: calendarData.currentMonthFirstDay, amount: -1 });
		const previousMonthFirstDay = getFirstDayOfMonth({ date: previousMonth });
		const newCalendarData = createCalendarData({ initialDate: previousMonthFirstDay, numberOfMonths });

		setCalendarData(newCalendarData);
	}, [calendarData, numberOfMonths]);

	return {
		calendarData,
		onSetNextMonth,
		onSetPreviousMonth,
	};
};
